import { Box, Container, Stack } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../../global/components";
import CustomTabs from "../../../../global/components/CustomTabs/CustomTabs1";
import strings from "../../../../global/constants/StringConstants";
import { handleErrorMessage } from "../../../../helpers/methods";
import { selectProfileDetails } from "../../../../redux/authSlice";
import { useAppSelector } from "../../../../utils/hooks";
import { theme } from "../../../../utils/styles";
import { getRahuTransits } from "../../../Shared/Dialogs/RahuTransitDialog/RahuTransitDialogServices";
import rahuTransitsStyles from "./RahuTransits.styles";
import Nakshatra from "./components/Nakshatra";
import Pada from "./components/Pada";
import Zodiac from "./components/Zodiac";

interface CustomProps {
  setIsLoading: Function;
  isLoading: boolean;
}

const RahuTransits = (props: CustomProps) => {
  const classes = rahuTransitsStyles;
  const profileDetails = useAppSelector(selectProfileDetails);
  const { i18n } = useTranslation();
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [selectedDate, setSelectedDate] = useState<string>(date);
  const [tabValue, setTabValue] = useState(`${t("zodiacTab")}`);
  const [rahuTransitData, setRahuTransitData] = useState({
    mean_rahu: {
      global: {
        zodiac: {},
        nakshtra: [],
        pada: [],
      },
      user: {
        zodiac: {},
        nakshtra: [],
        pada: [],
      },
    },
    true_rahu: {
      global: {
        zodiac: {},
        nakshtra: [],
        pada: [],
      },
      user: {
        zodiac: {},
        nakshtra: [],
        pada: [],
      },
    },
  });

  const tabConfig = [
    {
      label: `${t("zodiacTab")}`,
    },
    {
      label: `${t("nakshatraTab")}`,
    },
    {
      label: `${t("padaTab")}`,
    },
  ];

  useEffect(() => {
    getRahuTransitsData();
  }, [profileDetails, i18n.language]);

  const getRahuTransitsData = async () => {
    try {
      props.setIsLoading(true);

      const obj = {
        date,
        latitude: +profileDetails?.latitude,
        longitude: +profileDetails?.longitude,
        id: profileDetails?.profileId,
      };
      const language = i18n.language.toLowerCase();
      const response = await getRahuTransits(obj, language);
      setSelectedDate(date);
      setRahuTransitData(response);
      props.setIsLoading(false);
    } catch (error: any) {
      props.setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleChange = (newValue: string) => {
    setTabValue(newValue);
  };

  const getHeaderContent = () => {
    return (
      <Stack
        direction={{ sm: "row", xs: "column" }}
        spacing={{ sm: 2, xs: 1 }}
        justifyContent={"center"}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={dayjs(date, "YYYY-MM-DD")}
            onChange={(newValue: any) => {
              const formattedDate = newValue.format("YYYY-MM-DD");
              setDate(formattedDate);
            }}
            sx={classes.datePicker}
          />
        </LocalizationProvider>
        <CustomButton
          label={t("calculate")}
          type={strings.PRIMARY}
          onClick={getRahuTransitsData}
          customClasses={{
            width: "fit-content",
            [theme.breakpoints.down("sm")]: {
              fontSize: "14px",
              py: "6px",
              width: "100%",
            },
          }}
        />
      </Stack>
    );
  };

  const getComponentBasedOnValue = () => {
    switch (tabValue) {
      case `${t("zodiacTab")}`:
        return (
          <Zodiac
            meanZodiac={rahuTransitData.mean_rahu.user.zodiac}
            trueZodiac={rahuTransitData.true_rahu.user.zodiac}
            isLoading={props.isLoading}
            selectedDate={selectedDate}
          />
        );
      case `${t("nakshatraTab")}`:
        return (
          <Nakshatra
            meanNakshatra={rahuTransitData.mean_rahu.user.nakshtra}
            trueNakshatra={rahuTransitData.true_rahu.user.nakshtra}
            isLoading={props.isLoading}
          />
        );
      case `${t("padaTab")}`:
        return (
          <Pada
            meanPada={rahuTransitData.mean_rahu.user.pada}
            truePada={rahuTransitData.true_rahu.user.pada}
            isLoading={props.isLoading}
          />
        );
      default:
        return (
          <Zodiac
            meanZodiac={rahuTransitData.mean_rahu.user.zodiac}
            trueZodiac={rahuTransitData.true_rahu.user.zodiac}
            isLoading={props.isLoading}
            selectedDate={selectedDate}
          />
        );
    }
  };

  const getTabs = () => {
    return (
      <>
        <Box py={2.5}>
          <CustomTabs
            changeValue={handleChange}
            selected={tabValue}
            tabConfig={tabConfig}
          />
        </Box>
        {getComponentBasedOnValue()}
      </>
    );
  };

  const getContent = () => {
    return (
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Stack direction={"column"} spacing={2} mb={2}>
          {[1, 2].map((desc) => {
            return (
              <Box key={desc} sx={classes.legendDescText}>
                {t(`rahuTransitDesc${desc}`)}
              </Box>
            );
          })}
        </Stack>
        {getHeaderContent()}
        {getTabs()}
      </Container>
    );
  };

  return getContent();
};

export default RahuTransits;
